import { EmptyState } from '@/components/EmptyState';
import { FillLines } from '@/components/fill/FillLines';
import { SharedLines } from '@/components/fill/SharedLines';
import { FILL_CONTROLS_NONE } from '@/lib/fillPermissions';
import { NO_CLOSED_SHORT } from '@/lib/shortfallNote';
import { useFillSheet } from '@/components/fill/useFillSheet';
import { fillAudienceFor, actingOrgUnitTypeOf } from '@/lib/accessRole';
import { useAuth } from '@/hooks/useAuth';
import { DUTY_FILL_COPY } from '@/pages/DutyFill/copy';

/* ⚠️ A LOCAL STRING, because `DUTY_FILL_COPY` has no loading sentence — that screen gates the
   whole page on a spinner instead. Reusing `empty` here would claim the server had answered. */
const ROSTER_LOADING = 'Loading the roster…';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3b §2] WHO IS ON THIS DUTY — THE FACE VIEW, READ ONLY.
 *
 *  The arrangement view rendered `ForceMatrix`, a table of `n of m` cells. A table answers
 *  "how many" and this screen is where somebody asks "who": the fill view puts the actual
 *  names on chips, with the rank and the phone behind a hover card, which is the thing an
 *  operator was reaching for. Same component, same slots, same colours — the fill screen's UI
 *  with every write affordance withheld.
 *
 *  ⚠️ [amended C3c §3d] `FILL_CONTROLS_NONE` IS HARD-CODED AND IS NOT A PROP. `DutyOfficers` on the (hidden) record
 *  page passes `!canWrite`, because that surface is where a swap is performed; this one is a
 *  VIEW, and a prop would be a default somebody flips. The three write callbacks are still
 *  REQUIRED by `FillLines`, so they are supplied as no-ops rather than omitted — which is also
 *  what makes "view only" a property of this file rather than of a caller's argument.
 *
 *  ⚠️ IT IS GATED ON `enabled`, AND THAT IS THE DIFFERENCE BETWEEN ONE REQUEST AND TWENTY-FOUR.
 *  `MAX_DUTY_POINTS` is 24 and the accordions render for every card on mount, so an ungated
 *  `useFillSheet` would fire a sheet read per card the moment the page opened — on the heaviest
 *  screen in the product. The host passes the accordion's own `open`, so a card that has never
 *  been expanded costs nothing.
 *
 *  ⚠️ AND THE AUDIENCE IS DERIVED, NOT HARD-CODED `'oversight'`. [UX1 §3c]'s rule: a post reads
 *  differently to two ROLES, not on two screens. Hard-coding it here would let the same person
 *  see one post green on the fill screen and amber two clicks away, which is a difference nobody
 *  could explain.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DutyRoster = ({ dutyId, enabled }: { dutyId: string; enabled: boolean }): React.ReactElement => {
  const { officer } = useAuth();
  const { data, sheet } = useFillSheet(dutyId, { enabled });

  const lines = data?.lines ?? [];
  /* [C5c §2h] Covered columns — a duty whose only force is shared is not empty. */
  const sharedLines = data?.sharedLines ?? [];

  /* ⚠️ Distinguishes "not fetched yet" from "this duty asks for nobody" — an empty state on a
     pending query is a claim the server has not made. */
  if (lines.length === 0 && sharedLines.length === 0) {
    return sheet.isPending ? (
      <p className="text-xs text-muted-foreground">{ROSTER_LOADING}</p>
    ) : (
      <EmptyState size="sm" title={DUTY_FILL_COPY.empty} />
    );
  }

  return (
    <>
      <FillLines
        lines={lines}
        staged={new Map()}
        cursor={null}
        controls={FILL_CONTROLS_NONE}
        audience={fillAudienceFor(officer?.accessRole ?? 'NONE', actingOrgUnitTypeOf(officer))}
        /*
         * ⚠️ [P7A §4] HARD-CODED, LIKE `FILL_CONTROLS_NONE`. The record page states a declared close ONCE,
         * in the notes beneath this roster (`ShortfallNotes`), with the counts and the same sentence as
         * the preview and the paper; a second line here would say it twice on one card.
         */
        closedShortReasons={NO_CLOSED_SHORT}
        /* ⚠️ REQUIRED BY THE CONTRACT AND DELIBERATELY INERT — see the header. `FILL_CONTROLS_NONE` already
         withholds every control that could call them; these exist so the type is satisfied
         without making the read-only-ness depend on a caller remembering to pass nothing. */
        onAdd={() => undefined}
        onSwap={() => undefined}
        onRemove={() => undefined}
      />
      {sharedLines.length > 0 ? <SharedLines lines={sharedLines} /> : null}
    </>
  );
};
